import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef, useState } from 'react';
const WebSocketContext = createContext(null);
export const WebSocketProvider = ({ children, autoReconnect = true, reconnectInterval = 5000, maxReconnectAttempts = 5, }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState(null);
    const wsRef = useRef(null);
    const subscribersRef = useRef(new Map());
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimeoutRef = useRef(null);
    const urlRef = useRef(null);
    const tokenRef = useRef(null);
    const cleanup = () => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        setIsConnected(false);
        setIsConnecting(false);
    };
    const handleMessage = (event) => {
        try {
            const message = JSON.parse(event.data);
            const subscribers = subscribersRef.current.get(message.type);
            if (subscribers) {
                subscribers.forEach(handler => {
                    try {
                        handler(message.data);
                    }
                    catch (error) {
                        console.error('Error in WebSocket message handler:', error);
                    }
                });
            }
        }
        catch (error) {
            console.error('Error parsing WebSocket message:', error);
        }
    };
    const handleOpen = () => {
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
        reconnectAttemptsRef.current = 0;
    };
    const handleClose = (event) => {
        setIsConnected(false);
        setIsConnecting(false);
        if (!event.wasClean && autoReconnect && reconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = reconnectInterval * Math.pow(2, reconnectAttemptsRef.current);
            reconnectTimeoutRef.current = setTimeout(() => {
                reconnectAttemptsRef.current++;
                if (urlRef.current) {
                    connectInternal(urlRef.current, tokenRef.current || undefined);
                }
            }, delay);
        }
        else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
            setError('Max reconnection attempts reached');
        }
    };
    const handleError = (event) => {
        console.error('WebSocket error:', event);
        setError('WebSocket connection error');
        setIsConnecting(false);
    };
    const connect = (url, token) => {
        // Check if already connected or connecting
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            return;
        }
        if (wsRef.current?.readyState === WebSocket.CONNECTING) {
            return;
        }
        connectInternal(url, token);
    };
    const reconnect = (url, token) => {
        const targetUrl = url || urlRef.current;
        const targetToken = token || tokenRef.current || undefined;
        if (!targetUrl) {
            console.warn('WebSocket reconnect: no URL available');
            return;
        }
        reconnectAttemptsRef.current = 0;
        connectInternal(targetUrl, targetToken);
    };
    const connectInternal = (url, token) => {
        cleanup();
        setIsConnecting(true);
        setError(null);
        urlRef.current = url;
        tokenRef.current = token || null;
        try {
            const wsUrl = token ? `${url}?token=${encodeURIComponent(token)}` : url;
            wsRef.current = new WebSocket(wsUrl);
            wsRef.current.addEventListener('open', handleOpen);
            wsRef.current.addEventListener('message', handleMessage);
            wsRef.current.addEventListener('close', handleClose);
            wsRef.current.addEventListener('error', handleError);
        }
        catch (error) {
            console.error('Error creating WebSocket connection:', error);
            setError('Failed to create WebSocket connection');
            setIsConnecting(false);
        }
    };
    const disconnect = () => {
        reconnectAttemptsRef.current = maxReconnectAttempts; // Prevent auto-reconnect
        cleanup();
    };
    const sendMessage = (message) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            try {
                wsRef.current.send(JSON.stringify(message));
            }
            catch (error) {
                console.error('Error sending WebSocket message:', error);
                setError('Failed to send message');
            }
        }
        else {
            console.warn('WebSocket not connected, cannot send message');
        }
    };
    const subscribe = (type, handler) => {
        if (!subscribersRef.current.has(type)) {
            subscribersRef.current.set(type, new Set());
        }
        subscribersRef.current.get(type).add(handler);
        // Return unsubscribe function
        return () => {
            const subscribers = subscribersRef.current.get(type);
            if (subscribers) {
                subscribers.delete(handler);
                if (subscribers.size === 0) {
                    subscribersRef.current.delete(type);
                }
            }
        };
    };
    useEffect(() => {
        return () => {
            cleanup();
        };
    }, []);
    const contextValue = {
        isConnected,
        isConnecting,
        error,
        sendMessage,
        subscribe,
        connect,
        reconnect,
        disconnect,
    };
    return (_jsx(WebSocketContext.Provider, { value: contextValue, children: children }));
};
export const useWebSocket = () => {
    const context = useContext(WebSocketContext);
    if (!context) {
        throw new Error('useWebSocket must be used within a WebSocketProvider');
    }
    return context;
};
//# sourceMappingURL=WebSocketProvider.js.map